import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Save, Plus, Trash2, Upload, X } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

type Machine = {
  id: string;
  name: string;
  venueName?: string;
};

type LocationTenderData = {
  id?: string;
  venueName: string;
  venueType?: string;
  slug: string;
  description?: string;
  location?: string;
  startingPriceCents?: number;
  metrics?: any;
  heroImages?: string[];
  machineIds?: string[];
  Machines?: Machine[];
  isActive: boolean;
};

// Migration function to convert old age demographics keys to new ones
function migrateAgeDemographics(data: Record<string, any> | undefined): {
  'Under 16': string;
  '16-24': string;
  '25-44': string;
  '45-64': string;
  '65+': string;
} {
  const migrated: {
    'Under 16': string;
    '16-24': string;
    '25-44': string;
    '45-64': string;
    '65+': string;
  } = {
    'Under 16': '',
    '16-24': '',
    '25-44': '',
    '45-64': '',
    '65+': '',
  };

  if (!data) {
    return migrated;
  }

  // Map old keys to new keys
  const oldToNewMap: Record<string, string> = {
    '18-24': '16-24',
    '25-34': '25-44',
    '35-44': '25-44',
    '45-54': '45-64',
    '55+': '65+',
  };

  Object.entries(data).forEach(([key, value]) => {
    const newKey = oldToNewMap[key] || key;
    const numValue = typeof value === 'string' ? parseFloat(value) : (typeof value === 'number' ? value : 0);
    
    // If it's a new key, use it directly
    if (key in migrated) {
      (migrated as any)[key] = typeof value === 'string' ? value : (value?.toString() || '');
    } 
    // If it's an old key that maps to a new key, combine values
    else if (newKey in migrated && !isNaN(numValue) && numValue > 0) {
      const existingValue = migrated[newKey as keyof typeof migrated] ? parseFloat(migrated[newKey as keyof typeof migrated]) : 0;
      const combinedValue = (!isNaN(existingValue) ? existingValue : 0) + numValue;
      (migrated as any)[newKey] = combinedValue.toString();
    }
  });

  return migrated;
}

export default function AdminLocationTenderEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [formData, setFormData] = useState<LocationTenderData>({
    venueName: '',
    venueType: '',
    slug: '',
    description: '',
    location: '',
    startingPriceCents: undefined,
    heroImages: [],
    machineIds: [],
    isActive: true,
  });

  // Metric checkboxes and values
  const [metrics, setMetrics] = useState({
    enableNumberOfMachines: false,
    numberOfMachines: '',
    enableNumberOfLocations: false,
    numberOfLocations: '',
    locationBreakdown: [] as Array<{ name: string; machineCount: string }>,
    enableFootfallPerAnnum: false,
    footfallPerAnnum: '',
    enableImpressionsPerMachine: false,
    impressionsPerMachine: '',
    enableMachineSpecs: false,
    machineSpecs: { screenSize: '', resolution: '', orientation: '' },
    enableAbc1Percentage: false,
    abc1Percentage: '',
    enableAverageDwellTime: false,
    averageDwellTimeMinutes: '',
    enableGenderSplit: false,
    genderFemale: '',
    genderMale: '',
    enableAverageSpendPerVisit: false,
    averageSpendPerVisitCents: '',
    enableWeeklyFootfall: false,
    weeklyFootfall: '',
    weeklyFootfallBreakdown: {
      monday: '',
      tuesday: '',
      wednesday: '',
      thursday: '',
      friday: '',
      saturday: '',
      sunday: '',
    },
    enableAgeDemographics: false,
    ageDemographics: {
      'Under 16': '',
      '16-24': '',
      '25-44': '',
      '45-64': '',
      '65+': '',
    },
    enableWeeklyExpectedSales: false,
    weeklyExpectedSales: '',
    enableMonthlyMachineInteractions: false,
    monthlyMachineInteractions: '',
    enableWeeklyMachineInteractions: false,
    weeklyMachineInteractions: '',
    enableDailyMachineInteractions: false,
    dailyMachineInteractions: '',
  });

  useEffect(() => {
    loadMachines();
    if (isEditing) {
      loadLocationTender();
    }
  }, [id]);

  async function loadMachines() {
    try {
      const res = await fetch('/api/store/machines', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setMachines(data);
      }
    } catch (error) {
      console.error('Failed to load machines:', error);
    }
  }

  async function loadLocationTender() {
    if (!id) return;
    try {
      setLoading(true);
      const res = await fetch(`/api/admin/location-tenders/${id}`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setFormData({
          id: data.id,
          venueName: data.venueName || '',
          venueType: data.venueType || '',
          slug: data.slug || '',
          description: data.description || '',
          location: data.location || '',
          startingPriceCents: data.startingPriceCents,
          heroImages: data.heroImages || [],
          machineIds: data.machineIds || [],
          isActive: data.isActive !== undefined ? data.isActive : true,
        });

        // Load metrics
        if (data.metrics) {
          const m = data.metrics;
          const enabledMetrics = m.enabledMetrics || [];
          setMetrics({
            enableNumberOfMachines: enabledMetrics.includes('numberOfMachines'),
            numberOfMachines: m.numberOfMachines?.toString() || '',
            enableNumberOfLocations: enabledMetrics.includes('numberOfLocations'),
            numberOfLocations: m.numberOfLocations?.toString() || '',
            locationBreakdown: m.locationBreakdown && Array.isArray(m.locationBreakdown)
              ? m.locationBreakdown.map((loc: any) => ({
                  name: loc.name || '',
                  machineCount: loc.machineCount?.toString() || '',
                }))
              : [],
            enableFootfallPerAnnum: enabledMetrics.includes('footfallPerAnnum'),
            footfallPerAnnum: m.footfallPerAnnum?.toString() || '',
            enableImpressionsPerMachine: enabledMetrics.includes('impressionsPerMachine'),
            impressionsPerMachine: m.impressionsPerMachine?.toString() || '',
            enableMachineSpecs: enabledMetrics.includes('machineSpecs'),
            machineSpecs: m.machineSpecs || { screenSize: '', resolution: '', orientation: '' },
            enableAbc1Percentage: enabledMetrics.includes('abc1Percentage'),
            abc1Percentage: m.abc1Percentage?.toString() || '',
            enableAverageDwellTime: enabledMetrics.includes('averageDwellTimeMinutes'),
            averageDwellTimeMinutes: m.averageDwellTimeMinutes?.toString() || '',
            enableGenderSplit: enabledMetrics.includes('genderFemale') || enabledMetrics.includes('genderMale'),
            genderFemale: m.genderFemale?.toString() || '',
            genderMale: m.genderMale?.toString() || '',
            enableAverageSpendPerVisit: enabledMetrics.includes('averageSpendPerVisitCents'),
            averageSpendPerVisitCents: m.averageSpendPerVisitCents ? (m.averageSpendPerVisitCents / 100).toFixed(2) : '',
            enableWeeklyFootfall: enabledMetrics.includes('weeklyFootfall'),
            weeklyFootfall: m.weeklyFootfall?.toString() || '',
            weeklyFootfallBreakdown: m.weeklyFootfallBreakdown || {
              monday: '', tuesday: '', wednesday: '', thursday: '', friday: '', saturday: '', sunday: '',
            },
            enableAgeDemographics: enabledMetrics.includes('ageDemographics'),
            ageDemographics: migrateAgeDemographics(m.ageDemographics),
            enableWeeklyExpectedSales: enabledMetrics.includes('weeklyExpectedSales'),
            weeklyExpectedSales: m.weeklyExpectedSales ? (m.weeklyExpectedSales / 100).toFixed(2) : '',
            enableMonthlyMachineInteractions: enabledMetrics.includes('monthlyMachineInteractions'),
            monthlyMachineInteractions: m.monthlyMachineInteractions?.toString() || '',
            enableWeeklyMachineInteractions: enabledMetrics.includes('weeklyMachineInteractions'),
            weeklyMachineInteractions: m.weeklyMachineInteractions?.toString() || '',
            enableDailyMachineInteractions: enabledMetrics.includes('dailyMachineInteractions'),
            dailyMachineInteractions: m.dailyMachineInteractions?.toString() || '',
          });
        }
      }
    } catch (error) {
      console.error('Failed to load location tender:', error);
      alert('Failed to load location tender');
    } finally {
      setLoading(false);
    }
  }

  async function handleImageUpload(files: File[]) {
    if (files.length === 0) return;
    
    setUploadingImages(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('image', file);

        const res = await fetch('/api/upload/image', {
          method: 'POST',
          credentials: 'include',
          body: formData,
        });

        if (!res.ok) {
          throw new Error(`Failed to upload ${file.name}`);
        }

        const data = await res.json();
        return data.url;
      });

      const uploadedUrls = await Promise.all(uploadPromises);
      setFormData({
        ...formData,
        heroImages: [...(formData.heroImages || []), ...uploadedUrls],
      });
    } catch (error) {
      console.error('Error uploading images:', error);
      alert(`Failed to upload images: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setUploadingImages(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);

    try {
      // Prepare payload
      const payload: any = {
        venueName: formData.venueName,
        venueType: formData.venueType || null,
        description: formData.description || null,
        location: formData.location || null,
        startingPriceCents: formData.startingPriceCents ? Math.round(formData.startingPriceCents * 100) : null,
        heroImages: formData.heroImages || [],
        machineIds: formData.machineIds || [],
        isActive: formData.isActive,
      };

      // Add metric checkboxes and values
      Object.keys(metrics).forEach((key) => {
        payload[key] = metrics[key as keyof typeof metrics];
      });

      // Convert currency values to cents
      if (metrics.enableAverageSpendPerVisit && metrics.averageSpendPerVisitCents) {
        payload.averageSpendPerVisitCents = Math.round(parseFloat(metrics.averageSpendPerVisitCents) * 100);
      }
      if (metrics.enableWeeklyExpectedSales && metrics.weeklyExpectedSales) {
        payload.weeklyExpectedSales = parseFloat(metrics.weeklyExpectedSales) * 100;
      }

      const url = isEditing
        ? `/api/admin/location-tenders/${id}`
        : '/api/admin/location-tenders';
      const method = isEditing ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        const data = await res.json();
        navigate('/admin/location-tenders');
      } else {
        const error = await res.json();
        alert(error.message || 'Failed to save location tender');
      }
    } catch (error) {
      console.error('Failed to save:', error);
      alert('Failed to save location tender');
    } finally {
      setSaving(false);
    }
  }

  // Filter machines by venue name when venue name is set
  const availableMachines = formData.venueName
    ? machines.filter((m) => m.venueName === formData.venueName)
    : machines;

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="outline" onClick={() => navigate('/admin/location-tenders')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <h1 className="text-2xl font-bold">
          {isEditing ? 'Edit Location Tender' : 'Create Location Tender'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="venueName">Venue Name *</Label>
              <Input
                id="venueName"
                value={formData.venueName}
                onChange={(e) => setFormData({ ...formData, venueName: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="venueType">Venue Type</Label>
              <Input
                id="venueType"
                value={formData.venueType}
                onChange={(e) => setFormData({ ...formData, venueType: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
              />
            </div>

            <div>
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="e.g., London, England"
              />
            </div>

            <div>
              <Label htmlFor="startingPrice">Starting Price (£)</Label>
              <Input
                id="startingPrice"
                type="number"
                step="0.01"
                value={formData.startingPriceCents ? (formData.startingPriceCents / 100).toFixed(2) : ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    startingPriceCents: e.target.value ? parseFloat(e.target.value) * 100 : undefined,
                  })
                }
              />
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="isActive"
                checked={formData.isActive}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, isActive: checked === true })
                }
              />
              <Label htmlFor="isActive">Active</Label>
            </div>
          </CardContent>
        </Card>

        {/* Hero Images */}
        <Card>
          <CardHeader>
            <CardTitle>Hero Images</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Drag and drop area */}
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setIsDragging(false);
              }}
              onDrop={async (e) => {
                e.preventDefault();
                setIsDragging(false);
                const files = Array.from(e.dataTransfer.files).filter(
                  file => file.type.startsWith('image/')
                );
                await handleImageUpload(files);
              }}
              className={`
                border-2 border-dashed rounded-lg p-8 text-center transition-colors
                ${isDragging ? 'border-brand-primary bg-brand-primary/5' : 'border-muted-foreground/25'}
                ${uploadingImages ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}
              `}
              onClick={() => document.getElementById('hero-image-input')?.click()}
            >
              <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-lg font-medium mb-2">
                Drag and drop images here, or click to select
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                Supports multiple images (max 5MB each)
              </p>
              <Input
                id="hero-image-input"
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  if (e.target.files) {
                    handleImageUpload(Array.from(e.target.files));
                  }
                }}
                className="hidden"
              />
            </div>

            {/* Image previews */}
            {formData.heroImages && formData.heroImages.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {formData.heroImages.map((url, index) => (
                  <div key={index} className="relative group">
                    <div className="aspect-video rounded-lg overflow-hidden border border-muted bg-muted">
                      <img
                        src={url}
                        alt={`Hero ${index + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect width="100" height="100" fill="%23ddd"/%3E%3Ctext x="50" y="50" text-anchor="middle" dy=".3em" fill="%23999"%3EImage%3C/text%3E%3C/svg%3E';
                        }}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => {
                        const newImages = formData.heroImages?.filter((_, i) => i !== index) || [];
                        setFormData({ ...formData, heroImages: newImages });
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Machine Selection */}
        <Card>
          <CardHeader>
            <CardTitle>Machines</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Select Machines</Label>
              <div className="border rounded-md p-4 max-h-64 overflow-y-auto">
                {availableMachines.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    {formData.venueName
                      ? 'No machines found for this venue. Enter a venue name first.'
                      : 'Enter a venue name to filter machines.'}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {availableMachines.map((machine) => (
                      <div key={machine.id} className="flex items-center gap-2">
                        <Checkbox
                          checked={formData.machineIds?.includes(machine.id) || false}
                          onCheckedChange={(checked) => {
                            const currentIds = formData.machineIds || [];
                            if (checked) {
                              setFormData({
                                ...formData,
                                machineIds: [...currentIds, machine.id],
                              });
                            } else {
                              setFormData({
                                ...formData,
                                machineIds: currentIds.filter((id) => id !== machine.id),
                              });
                            }
                          }}
                        />
                        <Label>{machine.name}</Label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Dynamic Metrics Section */}
        <Card>
          <CardHeader>
            <CardTitle>Metrics & Analytics</CardTitle>
            <p className="text-sm text-muted-foreground">
              Select which metrics to display on the public page
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Number of Machines */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="enableNumberOfMachines"
                  checked={metrics.enableNumberOfMachines}
                  onCheckedChange={(checked) =>
                    setMetrics({ ...metrics, enableNumberOfMachines: checked === true })
                  }
                />
                <Label htmlFor="enableNumberOfMachines">Number of Machines</Label>
              </div>
              {metrics.enableNumberOfMachines && (
                <Input
                  type="number"
                  value={metrics.numberOfMachines}
                  onChange={(e) => setMetrics({ ...metrics, numberOfMachines: e.target.value })}
                  placeholder="Enter number"
                />
              )}
            </div>

            {/* Number of Locations */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="enableNumberOfLocations"
                  checked={metrics.enableNumberOfLocations}
                  onCheckedChange={(checked) =>
                    setMetrics({ ...metrics, enableNumberOfLocations: checked === true })
                  }
                />
                <Label htmlFor="enableNumberOfLocations">Number of Locations</Label>
              </div>
              {metrics.enableNumberOfLocations && (
                <div className="space-y-3">
                  <Input
                    type="number"
                    value={metrics.numberOfLocations}
                    onChange={(e) => setMetrics({ ...metrics, numberOfLocations: e.target.value })}
                    placeholder="Enter number"
                  />
                  
                  {/* Location Breakdown */}
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold text-brand-navy">Location Breakdown (Optional)</Label>
                    <div className="space-y-2">
                      {metrics.locationBreakdown.map((location, index) => (
                        <div key={index} className="flex gap-2">
                          <Input
                            type="text"
                            value={location.name}
                            onChange={(e) => {
                              const newBreakdown = [...metrics.locationBreakdown];
                              newBreakdown[index] = { ...newBreakdown[index], name: e.target.value };
                              setMetrics({ ...metrics, locationBreakdown: newBreakdown });
                            }}
                            placeholder="Location name (e.g., Manchester Airport)"
                            className="flex-1"
                          />
                          <Input
                            type="number"
                            value={location.machineCount}
                            onChange={(e) => {
                              const newBreakdown = [...metrics.locationBreakdown];
                              newBreakdown[index] = { ...newBreakdown[index], machineCount: e.target.value };
                              setMetrics({ ...metrics, locationBreakdown: newBreakdown });
                            }}
                            placeholder="Machines"
                            className="w-24"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => {
                              const newBreakdown = metrics.locationBreakdown.filter((_, i) => i !== index);
                              setMetrics({ ...metrics, locationBreakdown: newBreakdown });
                            }}
                            className="shrink-0"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setMetrics({
                            ...metrics,
                            locationBreakdown: [...metrics.locationBreakdown, { name: '', machineCount: '' }],
                          });
                        }}
                        className="w-full"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Location
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footfall per Annum */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="enableFootfallPerAnnum"
                  checked={metrics.enableFootfallPerAnnum}
                  onCheckedChange={(checked) =>
                    setMetrics({ ...metrics, enableFootfallPerAnnum: checked === true })
                  }
                />
                <Label htmlFor="enableFootfallPerAnnum">Footfall per Annum</Label>
              </div>
              {metrics.enableFootfallPerAnnum && (
                <Input
                  type="number"
                  value={metrics.footfallPerAnnum}
                  onChange={(e) => setMetrics({ ...metrics, footfallPerAnnum: e.target.value })}
                  placeholder="Enter number"
                />
              )}
            </div>

            {/* Impressions per Machine */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="enableImpressionsPerMachine"
                  checked={metrics.enableImpressionsPerMachine}
                  onCheckedChange={(checked) =>
                    setMetrics({ ...metrics, enableImpressionsPerMachine: checked === true })
                  }
                />
                <Label htmlFor="enableImpressionsPerMachine">Impressions per Machine</Label>
              </div>
              {metrics.enableImpressionsPerMachine && (
                <Input
                  type="number"
                  value={metrics.impressionsPerMachine}
                  onChange={(e) => setMetrics({ ...metrics, impressionsPerMachine: e.target.value })}
                  placeholder="Enter number"
                />
              )}
            </div>

            {/* Machine Specs */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="enableMachineSpecs"
                  checked={metrics.enableMachineSpecs}
                  onCheckedChange={(checked) =>
                    setMetrics({ ...metrics, enableMachineSpecs: checked === true })
                  }
                />
                <Label htmlFor="enableMachineSpecs">Machine Specs</Label>
              </div>
              {metrics.enableMachineSpecs && (
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    placeholder="Screen Size"
                    value={metrics.machineSpecs.screenSize}
                    onChange={(e) =>
                      setMetrics({
                        ...metrics,
                        machineSpecs: { ...metrics.machineSpecs, screenSize: e.target.value },
                      })
                    }
                  />
                  <Input
                    placeholder="Resolution"
                    value={metrics.machineSpecs.resolution}
                    onChange={(e) =>
                      setMetrics({
                        ...metrics,
                        machineSpecs: { ...metrics.machineSpecs, resolution: e.target.value },
                      })
                    }
                  />
                  <Input
                    placeholder="Orientation"
                    value={metrics.machineSpecs.orientation}
                    onChange={(e) =>
                      setMetrics({
                        ...metrics,
                        machineSpecs: { ...metrics.machineSpecs, orientation: e.target.value },
                      })
                    }
                  />
                </div>
              )}
            </div>

            {/* ABC1 Percentage */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="enableAbc1Percentage"
                  checked={metrics.enableAbc1Percentage}
                  onCheckedChange={(checked) =>
                    setMetrics({ ...metrics, enableAbc1Percentage: checked === true })
                  }
                />
                <Label htmlFor="enableAbc1Percentage">ABC1 Percentage</Label>
              </div>
              {metrics.enableAbc1Percentage && (
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={metrics.abc1Percentage}
                  onChange={(e) => setMetrics({ ...metrics, abc1Percentage: e.target.value })}
                  placeholder="Enter percentage (0-100)"
                />
              )}
            </div>

            {/* Average Dwell Time */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="enableAverageDwellTime"
                  checked={metrics.enableAverageDwellTime}
                  onCheckedChange={(checked) =>
                    setMetrics({ ...metrics, enableAverageDwellTime: checked === true })
                  }
                />
                <Label htmlFor="enableAverageDwellTime">Average Dwell Time (minutes)</Label>
              </div>
              {metrics.enableAverageDwellTime && (
                <Input
                  type="number"
                  value={metrics.averageDwellTimeMinutes}
                  onChange={(e) => setMetrics({ ...metrics, averageDwellTimeMinutes: e.target.value })}
                  placeholder="Enter minutes"
                />
              )}
            </div>

            {/* Gender Split */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="enableGenderSplit"
                  checked={metrics.enableGenderSplit}
                  onCheckedChange={(checked) =>
                    setMetrics({ ...metrics, enableGenderSplit: checked === true })
                  }
                />
                <Label htmlFor="enableGenderSplit">Gender Split</Label>
              </div>
              {metrics.enableGenderSplit && (
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={metrics.genderFemale}
                    onChange={(e) => setMetrics({ ...metrics, genderFemale: e.target.value })}
                    placeholder="Women %"
                  />
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={metrics.genderMale}
                    onChange={(e) => setMetrics({ ...metrics, genderMale: e.target.value })}
                    placeholder="Men %"
                  />
                </div>
              )}
            </div>

            {/* Average Spend per Visit */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="enableAverageSpendPerVisit"
                  checked={metrics.enableAverageSpendPerVisit}
                  onCheckedChange={(checked) =>
                    setMetrics({ ...metrics, enableAverageSpendPerVisit: checked === true })
                  }
                />
                <Label htmlFor="enableAverageSpendPerVisit">Average Spend per Visit (£)</Label>
              </div>
              {metrics.enableAverageSpendPerVisit && (
                <Input
                  type="number"
                  step="0.01"
                  value={metrics.averageSpendPerVisitCents}
                  onChange={(e) => setMetrics({ ...metrics, averageSpendPerVisitCents: e.target.value })}
                  placeholder="Enter amount"
                />
              )}
            </div>

            {/* Weekly Footfall */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="enableWeeklyFootfall"
                  checked={metrics.enableWeeklyFootfall}
                  onCheckedChange={(checked) =>
                    setMetrics({ ...metrics, enableWeeklyFootfall: checked === true })
                  }
                />
                <Label htmlFor="enableWeeklyFootfall">Weekly Footfall</Label>
              </div>
              {metrics.enableWeeklyFootfall && (
                <div className="space-y-2">
                  <Input
                    type="number"
                    value={metrics.weeklyFootfall}
                    onChange={(e) => setMetrics({ ...metrics, weeklyFootfall: e.target.value })}
                    placeholder="Total weekly footfall"
                  />
                  <div className="grid grid-cols-7 gap-2">
                    {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                      <div key={day}>
                        <Label className="text-xs capitalize">{day.slice(0, 3)}</Label>
                        <Input
                          type="number"
                          value={metrics.weeklyFootfallBreakdown[day as keyof typeof metrics.weeklyFootfallBreakdown]}
                          onChange={(e) =>
                            setMetrics({
                              ...metrics,
                              weeklyFootfallBreakdown: {
                                ...metrics.weeklyFootfallBreakdown,
                                [day]: e.target.value,
                              },
                            })
                          }
                          placeholder="%"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Age Demographics */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="enableAgeDemographics"
                  checked={metrics.enableAgeDemographics}
                  onCheckedChange={(checked) =>
                    setMetrics({ ...metrics, enableAgeDemographics: checked === true })
                  }
                />
                <Label htmlFor="enableAgeDemographics">Age Demographics (%)</Label>
              </div>
              {metrics.enableAgeDemographics && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {Object.keys(metrics.ageDemographics).map((age) => (
                    <div key={age}>
                      <Label className="text-xs">{age}</Label>
                      <Input
                        type="number"
                        value={metrics.ageDemographics[age as keyof typeof metrics.ageDemographics]}
                        onChange={(e) =>
                          setMetrics({
                            ...metrics,
                            ageDemographics: {
                              ...metrics.ageDemographics,
                              [age]: e.target.value,
                            },
                          })
                        }
                        placeholder="%"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Weekly Expected Sales */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="enableWeeklyExpectedSales"
                  checked={metrics.enableWeeklyExpectedSales}
                  onCheckedChange={(checked) =>
                    setMetrics({ ...metrics, enableWeeklyExpectedSales: checked === true })
                  }
                />
                <Label htmlFor="enableWeeklyExpectedSales">Weekly Expected Sales (£)</Label>
              </div>
              {metrics.enableWeeklyExpectedSales && (
                <Input
                  type="number"
                  step="0.01"
                  value={metrics.weeklyExpectedSales}
                  onChange={(e) => setMetrics({ ...metrics, weeklyExpectedSales: e.target.value })}
                  placeholder="Enter amount"
                />
              )}
            </div>

            {/* Machine Interactions */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="enableMachineInteractions"
                  checked={
                    metrics.enableMonthlyMachineInteractions ||
                    metrics.enableWeeklyMachineInteractions ||
                    metrics.enableDailyMachineInteractions
                  }
                  onCheckedChange={(checked) => {
                    const val = checked === true;
                    setMetrics({
                      ...metrics,
                      enableMonthlyMachineInteractions: val,
                      enableWeeklyMachineInteractions: val,
                      enableDailyMachineInteractions: val,
                    });
                  }}
                />
                <Label htmlFor="enableMachineInteractions">Machine Interactions</Label>
              </div>
              {(metrics.enableMonthlyMachineInteractions ||
                metrics.enableWeeklyMachineInteractions ||
                metrics.enableDailyMachineInteractions) && (
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">Monthly</Label>
                    <Input
                      type="number"
                      value={metrics.monthlyMachineInteractions}
                      onChange={(e) => setMetrics({ ...metrics, monthlyMachineInteractions: e.target.value })}
                      placeholder="Monthly"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Weekly</Label>
                    <Input
                      type="number"
                      value={metrics.weeklyMachineInteractions}
                      onChange={(e) => setMetrics({ ...metrics, weeklyMachineInteractions: e.target.value })}
                      placeholder="Weekly"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Daily</Label>
                    <Input
                      type="number"
                      value={metrics.dailyMachineInteractions}
                      onChange={(e) => setMetrics({ ...metrics, dailyMachineInteractions: e.target.value })}
                      placeholder="Daily"
                    />
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate('/admin/location-tenders')}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : isEditing ? 'Update' : 'Create'}
          </Button>
        </div>
      </form>
    </div>
  );
}
